/* eslint-disable react-refresh/only-export-components */
import { lazy } from "react";

/* =======================
   Layouts
======================= */
import PagesRootLayout from "common/root-layouts/PagesRootLayout";
import RootLayoutCover from "common/root-layouts/RootLayoutCover";
import AuthUserRoot from "common/root-layouts/AuthUserRoot";
import NotFound from "pages/not-found";
import TraineesRoot from "pages/user-pages/trainees/TraineesRoot";
import DropInPlayersRoot from "pages/user-pages/players/DropInPlayersRoot";

/* =======================
   Lazy Pages
======================= */
const Home = lazy(() => import("pages/home"));
const Venues = lazy(() => import("pages/venues"));
const VenueDetails = lazy(() => import("pages/venue-details"));
const BookCourt = lazy(() => import("pages/book-court"));
const BookingQuery = lazy(() => import("pages/booking-query"));
const ParentalWaiver = lazy(() => import("pages/parental-waiver-form"));
// const CourtBooking = lazy(() => import("pages/court-booking"));
// const NewCourtBooking = lazy(() => import("pages/new-court-booking/NewCourtBooking"));
const NewCourtBookingFlow = lazy(() => import("pages/new-court-booking-flow"));
const Training = lazy(() => import("pages/training"));
const BuyTraningIndex = lazy(() => import("pages/training/BuyTraningIndex"));
const Pricing = lazy(() => import("pages/pricing"));
const BuyPlan = lazy(() => import("pages/membership"));
const BlogsPage = lazy(() => import("pages/blogs"));
const BlogDetailPage = lazy(() => import("pages/blog-detail"));
const AboutUs = lazy(() => import("pages/about-us"));
const ContactUs = lazy(() => import("pages/contact-us"));
const PrivacyPolicy = lazy(() => import("pages/privacy-policy"));
const TermsConditions = lazy(() => import("pages/terms-condition"));
const FAQs = lazy(() => import("pages/faqs"));
const SchoolFundraisingProgram = lazy(
  () => import("pages/school-fundraising-program"),
);
const BadmintonBeginnersPrograms = lazy(
  () => import("pages/badminton-beginners-programs"),
);
const PaDayBadmintonCamp = lazy(() => import("pages/pa-day"));
const WinterBadmintonCamp = lazy(() => import("pages/winter-camp"));
const DeleteAccount = lazy(() => import("pages/delete-account"));

/* flyer / landing pages (ads) */
const EtobicokeLanding = lazy(() => import("pages/flyer-pages/etobicoke"));

/* auth */
const LogIn = lazy(() => import("pages/authentication/login"));
const SignUp = lazy(() => import("pages/authentication/signup"));
const VerifyOTP = lazy(() => import("pages/authentication/verify-otp"));
const ForgotPassword = lazy(
  () => import("pages/authentication/forgot-password"),
);
const ResetPassword = lazy(() => import("pages/authentication/reset-password"));

/* user */
const Dashboard = lazy(() => import("pages/user-pages/dashboard"));
const MyBookings = lazy(() => import("pages/user-pages/my-bookings"));
const Profile = lazy(() => import("pages/user-pages/profile"));
const Trainees = lazy(() => import("pages/user-pages/trainees"));
const TraineeForm = lazy(() => import("pages/user-pages/trainees/TraineeForm"));
const ProfileSettings = lazy(() => import("pages/user-pages/profile-settings"));

/* drop-in */
const DropIn = lazy(() => import("pages/drop-in"));
const BuyDropinIndex = lazy(() => import("pages/drop-in/BuyDropinIndex"));
const DropInPlayers = lazy(() => import("pages/user-pages/players"));
const DropInPlayerForm = lazy(
  () => import("pages/user-pages/players/DropInPlayerForm"),
);

/* =======================
   Forbidden Routes Config
======================= */
import { CONFIG } from "config";
const forbidden_routes = CONFIG.forbidden_routes;

/* =======================
   Route Filter Function
======================= */
const filterRoutes = (routes, forbidden) =>
  routes
    .filter((route) => !(route.key && forbidden[route.key]))
    .map((route) =>
      route.children
        ? { ...route, children: filterRoutes(route.children, forbidden) }
        : route,
    );

/* =======================
   ROUTES DEFINITION
======================= */
const routes = [
  {
    path: "/",
    element: <PagesRootLayout />,
    errorElement: <NotFound />,
    children: filterRoutes(
      [
        { index: true, element: <Home />, key: "home" },
        {
          path: "venues",
          key: "venues",
          children: [
            { index: true, element: <Venues /> },
            { path: ":venueId", element: <VenueDetails /> },
          ],
        },
        {
          path: "book-court",
          key: "book_court",
          children: [
            { index: true, element: <BookCourt /> },
            // { path: ":courtId", element: <CourtBooking /> },
            { path: ":courtId", element: <NewCourtBookingFlow /> },
          ],
        },
        {
          path: "booking-query",
          element: <BookingQuery />,
          key: "booking_query",
        },
        {
          path: "parental-consent-and-liability-waiver",
          element: <ParentalWaiver />,
          key: "parental_waiver",
        },
        {
          path: "membership",
          key: "membership",
          children: [
            { index: true, element: <Pricing /> },
            { path: ":planId", element: <BuyPlan /> },
          ],
        },
        {
          path: "drop-in",
          key: "drop_in",
          children: [
            { index: true, element: <DropIn /> },
            { path: ":dropInId", element: <BuyDropinIndex /> },
          ],
        },
        {
          path: "private-team-lessons",
          key: "training",
          children: [
            { index: true, element: <Training /> },
            { path: ":planId", element: <BuyTraningIndex /> },
          ],
        },
        {
          path: "blogs",
          key: "blogs",
          children: [
            { index: true, element: <BlogsPage /> },
            { path: ":blogId", element: <BlogDetailPage /> },
          ],
        },
        { path: "about-us", element: <AboutUs />, key: "about_us" },
        { path: "contact-us", element: <ContactUs />, key: "contact_us" },
        { path: "faqs", element: <FAQs />, key: "faqs" },
        {
          path: "privacy-policy",
          element: <PrivacyPolicy />,
          key: "privacy_policy",
        },
        {
          path: "terms-condition",
          element: <TermsConditions />,
          key: "terms_conditions",
        },
        {
          path: "school-fund-raising-program",
          element: <SchoolFundraisingProgram />,
          key: "school_fund_raising_program",
        },
        {
          path: "badminton-beginners-programs",
          element: <BadmintonBeginnersPrograms />,
          key: "badminton_beginners_programs",
        },
        {
          path: "pa-day",
          element: <PaDayBadmintonCamp />,
          key: "pa_day_badminton_camp",
        },
        {
          path: "winter-camp",
          element: <WinterBadmintonCamp />,
          key: "winter_badminton_camp",
        },
        {
          path: "delete-account",
          element: <DeleteAccount />,
          key: "delete_account",
        },
      ],
      forbidden_routes.layout,
    ),
  },

  {
    path: "",
    element: <RootLayoutCover />,
    errorElement: <NotFound />,
    children: filterRoutes(
      [
        { path: "login", element: <LogIn />, key: "login" },
        { path: "sign-up", element: <SignUp />, key: "sign_up" },
        { path: "verify-otp", element: <VerifyOTP />, key: "verify_otp" },
        {
          path: "forgot-password",
          element: <ForgotPassword />,
          key: "forgot_password",
        },
        {
          path: "reset-password",
          element: <ResetPassword />,
          key: "reset_password",
        },
      ],
      forbidden_routes.auth,
    ),
  },

  {
    path: "",
    element: <AuthUserRoot />,
    errorElement: <NotFound />,
    children: filterRoutes(
      [
        { path: "dashboard", element: <Dashboard />, key: "dashboard" },
        { path: "my-bookings", element: <MyBookings />, key: "my_bookings" },
        { path: "profile", element: <Profile />, key: "my_profile" },
        {
          path: "trainees",
          element: <TraineesRoot />,
          key: "trainees",
          children: [
            { index: true, element: <Trainees /> },
            { path: "add-trainee", element: <TraineeForm /> },
            { path: "edit-trainee/:id", element: <TraineeForm /> },
          ],
        },
        {
          path: "profile-settings",
          element: <ProfileSettings />,
          key: "profile_settings",
        },
        {
          path: "drop-in-players",
          element: <DropInPlayersRoot />,
          key: "drop_in_players",
          children: [
            { index: true, element: <DropInPlayers /> },
            { path: "add", element: <DropInPlayerForm /> },
            { path: "edit/:id", element: <DropInPlayerForm /> },
          ],
        },
      ],
      forbidden_routes.user,
    ),
  },
  {
    path: "etobicoke",
    element: <RootLayoutCover />,
    errorElement: <NotFound />,
    children: [
      {
        index: true,
        element: <EtobicokeLanding />,
      },
    ],
  },
];

export default routes;
